import { MONTHS_IN_YEAR } from '@/shared/constants/common';
import { getEffectivePriceUnitAmount } from '@/shared/lib/getEffectivePriceUnitAmount';
import { PaymentInterval, Plan, Price } from '@/shared/types/Billing';

import { getFormattedPlanPrice } from './getFormattedPlanPrice';

export type PlanCardPricing = {
  annualTotalCents?: number;
  currency: string;
  minutes: number;
  perMonth: number;
  price?: Price;
  strikePerMonth?: number;
  yearlySavingsCents: number;
  yearlySavingsPercent: number;
};

export const getPlanCardPricing = (plan: Plan, period: PaymentInterval): PlanCardPricing => {
  const findActivePrice = (interval: PaymentInterval) =>
    plan.prices.find((price) => price.status === 'active' && price.recurring?.interval === interval);

  const monthlyPrice = findActivePrice(PaymentInterval.Month);
  const annualPrice = findActivePrice(PaymentInterval.Year);
  const isYearly = period === PaymentInterval.Year;
  const price = (isYearly ? annualPrice : monthlyPrice) ?? annualPrice ?? monthlyPrice;

  const perMonth = price
    ? getFormattedPlanPrice({
        paymentInterval: price.recurring?.interval,
        price: getEffectivePriceUnitAmount(plan.category, price),
      })
    : 0;
  const monthlyPerMonth = monthlyPrice
    ? getFormattedPlanPrice({ paymentInterval: PaymentInterval.Month, price: monthlyPrice.unit_amount })
    : undefined;

  const annualTotalCents = annualPrice ? getEffectivePriceUnitAmount(plan.category, annualPrice) : undefined;
  const monthlyYearCents = monthlyPrice ? monthlyPrice.unit_amount * MONTHS_IN_YEAR : 0;
  const yearlySavingsCents =
    monthlyYearCents && annualTotalCents !== undefined ? Math.max(monthlyYearCents - annualTotalCents, 0) : 0;

  const monthlyMinutes = Number(plan.metadata.bundle_value);

  return {
    annualTotalCents,
    currency: price?.currency ?? 'usd',
    minutes: isYearly ? monthlyMinutes * MONTHS_IN_YEAR : monthlyMinutes,
    perMonth,
    price,
    strikePerMonth: monthlyPerMonth !== undefined && monthlyPerMonth > perMonth ? monthlyPerMonth : undefined,
    yearlySavingsCents,
    yearlySavingsPercent: yearlySavingsCents > 0 ? Math.round((yearlySavingsCents / monthlyYearCents) * 100) : 0,
  };
};
